import React, { useMemo, useState, useEffect } from 'react';
import { Target, Award, ArrowUpRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import ProjectCard from '../components/ProjectCard';
import ProjectDetail from '../components/ProjectDetail';
import CTASection from '../components/CTASection';
import { STATUS_FILTERS, COUNTRIES, IMAGES } from '../data/mock';
import { getProjects } from '../lib/api';
import { useConnect } from '../context/ConnectContext';
import useReveal from '../hooks/useReveal';

export default function InvestmentOpportunities() {
  const { openConnect } = useConnect();
  const [status, setStatus] = useState('All');
  const [country, setCountry] = useState('All');
  const [selected, setSelected] = useState(null);
  const [projects, setProjects] = useState([]);
  useReveal([status, country, projects]);

  useEffect(() => {
    getProjects()
      .then((data) => setProjects(data))
      .catch(() => setProjects([]));
  }, []);

  const filtered = useMemo(
    () =>
      projects.filter(
        (p) => (status === 'All' || p.status === status) && (country === 'All' || p.country === country)
      ),
    [status, country, projects]
  );

  return (
    <div>
      <PageHero
        eyebrow="Investment opportunities"
        lead="Diverse"
        title="Opportunities"
        subtitle="Committed to positively impacting our world by actively contributing to 9 of the UN's 17 Sustainable Development Goals."
        image={IMAGES.countryside}
      />

      {/* INTRO STATS */}
      <section className="py-16">
        <div className="container-fw grid gap-6 sm:grid-cols-3">
          {[
            { icon: Award, v: '9 / 17', l: 'UN SDGs contributed to' },
            { icon: Target, v: 'Up to 21%', l: 'Target Net IRR across projects' },
            { icon: ArrowUpRight, v: '6 countries', l: 'Georgia, India, Sri Lanka, UAE & more' },
          ].map((s, i) => (
            <div key={s.l} className="reveal flex items-center gap-4 rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6" data-delay={i * 80}>
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-farmway-gold/15 text-farmway-gold">
                <s.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="font-display text-xl font-black text-farmway-cream">{s.v}</p>
                <p className="text-xs text-farmway-cream/60">{s.l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FILTERS + GRID */}
      <section className="pb-24">
        <div className="container-fw">
          <div className="flex flex-col gap-6 border-b border-white/10 pb-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`rounded-full px-5 py-2 font-display text-xs font-bold uppercase tracking-wider transition-all ${
                    status === s ? 'bg-farmway-gold text-farmway-green' : 'border border-white/15 text-farmway-cream/70 hover:text-farmway-cream'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {COUNTRIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCountry(c)}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                    country === c ? 'bg-white/10 text-farmway-gold' : 'text-farmway-cream/60 hover:text-farmway-cream'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-farmway-cream/55">
            Showing <span className="text-farmway-cream">{filtered.length}</span> {filtered.length === 1 ? 'project' : 'projects'}
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p, i) => (
                <div key={p.id} className="reveal" data-delay={(i % 3) * 80}>
                  <ProjectCard project={p} onOpen={setSelected} />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-16 rounded-2xl border border-white/10 bg-farmway-green-mid/30 p-16 text-center">
              <p className="text-farmway-cream/60">No projects match these filters yet. Check back soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* PLATFORM TEASER */}
      <section className="relative overflow-hidden bg-farmway-green-dark py-24">
        <div className="container-fw grid items-center gap-12 lg:grid-cols-2">
          <div className="reveal grid grid-cols-2 gap-4">
            <img src={IMAGES.almondAerial} alt="" className="h-56 w-full rounded-2xl object-cover" />
            <img src={IMAGES.saffronField} alt="" className="mt-10 h-56 w-full rounded-2xl object-cover" />
          </div>
          <div className="reveal" data-delay="120">
            <span className="eyebrow">State of the art</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">Manage your portfolio</h2>
            <p className="mt-5 max-w-lg text-farmway-cream/75">
              The upcoming Farmway platform is here to guide you on your journey managing your
              investment portfolio, from discovery to yield.
            </p>
            <button onClick={openConnect} className="btn-gold mt-8">Request early access <ArrowUpRight className="h-4 w-4" /></button>
          </div>
        </div>
      </section>

      <CTASection />
      <ProjectDetail project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
